import React from 'react';

import {AisPageResolver, isAisPage} from 'components/ais/ais.utils';
import {
    ControlRatioSettingsRules,
    DOCUMENT_UPLOAD_TEMPLATES_NAME,
} from 'components/control-ratio-settings';
import {DirectoriesCompanyForm, DIRECTORIES_COMPANY_NAME} from 'components/directories-company-form';
import {DocumentUploadTemplate} from 'components/document-upload-template/document-upload-template';
import {ExternalLoadingPageResolver, isExternalLoadingPage} from 'components/external-loading/external-loading.utils';
import {EFormMode} from 'components/form';
import {FormEditModal} from 'components/form-edit-modal/form-edit-modal';
import {ReportConfiguration} from 'components/report-configuration';
import {REPORT_CONFIGURATION_NAME} from 'components/report-configuration/report-configuration.constants';
import {Table} from 'components/table';
import {ITableScroll} from 'components/table/table';
import {IPageTemplateProps} from 'pages';
import {FlexFieldsPage, FlexFieldsSegmentsPage} from 'pages/flex-fields-settings-page';
import {FLEX_FIELDS_SEGMENTS_NAME, FLEX_FIELDS_SETTINGS_NAME} from 'pages/flex-fields-settings-page/constants/flex-fields.constants';
import {EEntityType} from 'shared/constants/entities';
import {CONTROL_RATIO_RULES_ENTITY_NAME} from 'shared/constants/urls';
import {useQueryParams} from 'shared/utils/query-params/use-query-params.hook';
import {trimUrlSlashes} from 'shared/utils/url';

import {ETablePageQueryParams} from './constants/table-query-params.constants';
import {CustomFormModal, customFormsMapping, ECustomFormEntityName} from './custom-form-modal';
import {getTablePageTemplateType} from './utils/query-params-aware-table.utils';

export interface IFormAwareTableProps extends IPageTemplateProps {
    hideTitle?: boolean;
    referenceUrl: string;
    scroll?: ITableScroll;
}

export const QueryParamsAwareTable = ({
    url, hideTitle, entityType, entityName, referenceUrl, scroll,
}: IFormAwareTableProps) => {
    const {removeQueryParams, getQueryParams} = useQueryParams();
    const {
        entity,
        dffName,
        dffCode,
        entityType: queryEntityType,
    } = getQueryParams(Object.values(ETablePageQueryParams));

    const referenceUrlWithoutSlash = trimUrlSlashes(referenceUrl);

    const handleFormClose = () => {
        removeQueryParams(Object.values(ETablePageQueryParams));
    };

    // ais pages
    if (isAisPage({entityName})) {
        return (
            <AisPageResolver
                entity={entity}
                entityName={entityName}
                entityType={entityType}
                url={url}
                scroll={scroll}
                hideTitle={hideTitle}
                handleFormClose={handleFormClose}
            />
        );
    }

    // external loading pages
    if (isExternalLoadingPage({entityName})) {
        return (
            <ExternalLoadingPageResolver
                entityName={entityName}
                entityType={entityType}
                url={url}
                scroll={scroll}
                hideTitle={hideTitle}
            />
        );
    }

    const tablePageType = getTablePageTemplateType({
        entityName,
        entity,
        dffName,
        dffCode,
    });

    switch (tablePageType) {
    case REPORT_CONFIGURATION_NAME:
        return (
            entity ? (
                <ReportConfiguration
                    entityName={entityName}
                    templateCode={entity}
                />
            ) : null
        );
    case DIRECTORIES_COMPANY_NAME:
        return (
            entity ? (
                <DirectoriesCompanyForm
                    entityName={entityName}
                    entity={entity}
                />
            ) : null
        );
    case DOCUMENT_UPLOAD_TEMPLATES_NAME:
        return (
            entity ? (
                <DocumentUploadTemplate
                    entityName={entityName}
                    templateCode={entity}
                />
            ) : null
        );
    case CONTROL_RATIO_RULES_ENTITY_NAME:
        return (
            <ControlRatioSettingsRules
                entity={entity}
                handleFormClose={handleFormClose}
                referenceUrlWithoutSlash={referenceUrl}
                entityName={entityName}
            />
        );
    case FLEX_FIELDS_SETTINGS_NAME:
        return (
            dffName ? (
                <FlexFieldsPage
                    dffName={dffName}
                />
            ) : null
        );
    case FLEX_FIELDS_SEGMENTS_NAME:
        return (
            (dffName && dffCode) ? (
                <FlexFieldsSegmentsPage
                    dffName={dffName}
                    dffCode={dffCode}
                />
            ) : null
        );
    default:
        return (
            <>
                <Table
                    hideTitle={hideTitle}
                    entityName={entityName}
                    url={url}
                    entityType={entityType}
                    scroll={scroll}
                />
                {(() => {
                    if (!(entity && !Number.isNaN(parseInt(entity, 10)))) return null;
                    if (customFormsMapping[entityName as ECustomFormEntityName]) {
                        return (
                            <CustomFormModal
                                mode={EFormMode.EDIT}
                                entityId={entity}
                                entityName={entityName as ECustomFormEntityName}
                            />
                        );
                    }
                    return (
                        <FormEditModal
                            entityName={entityName}
                            entityType={queryEntityType as EEntityType || EEntityType.FORM}
                            onClose={handleFormClose}
                            referenceUrl={`${referenceUrlWithoutSlash}/${entity}`}
                        />
                    );
                })()}
            </>
        );
    }
};
